import { RoomData } from '@/store/entities/Room';
import { Module, VuexModule, Mutation } from 'vuex-module-decorators';
import { Room, RoomDTO } from '../entities/Room';
import { Game, GameDTO, GameModes } from '../entities/Game';
import { Player } from '../entities/Player'
import { Card } from '../entities/Card';
import { GamePlayer } from '@/store/entities/Player';
import { userModule } from '../index';
import { decideGameSnapshot, nextTimerValue, shouldApplyRoomSnapshot } from '../gameStateProtocol';

const isTerminalGameMode = (mode?: GameModes) =>
  mode === GameModes.FINISHED || mode === GameModes.CRASHED

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'SystemModule'
})
class SystemModule extends VuexModule {
  room: Room | null = null
  game: Game | null = null
  publicRooms: RoomData[] = []
  loading = false

  @Mutation
  setPublicRooms(rooms: RoomData[]) {
    this.publicRooms = rooms
  }

  @Mutation
  createRoom({ roomData }: { roomData: RoomDTO }) {
    if (this.room?.id === roomData.id) return
    const room = new Room(roomData)
    this.room = room
  }

  @Mutation
  updateRoom({ roomData }: { roomData: RoomDTO }) {
    const hasRoom = this.room

    if (!hasRoom) {
      // this.createRoom({ roomData })
      const room = new Room(roomData)
      this.room = room
    }

    const room = this.room

    if (!room) return
    const incomingRevision = roomData.revision ?? -1
    if (!shouldApplyRoomSnapshot(room.id, room.revision, roomData.id, incomingRevision)) return

    const oldPlayers = room.players.map(p => p)
    room.players = roomData.players.map(player => {
      const hasPlayer = oldPlayers.find(p => p.id === player.id)
      if (hasPlayer) {
        return new Player({
          ...hasPlayer,
          color: player.color,
          status: player.status || hasPlayer.status,
          name: userModule.id === player.id ? hasPlayer.name : player.name,
          ready: player.ready,
          sleep: player.sleep
        })
      }
      else return new Player(player)
    })

    room.isPlaying = roomData.isPlaying
    room.mode = roomData.mode
    room.difficulty = roomData.difficulty
    room.timer = roomData.timer
    room.cardsCount = roomData.cardsCount
    room.ownerId = roomData.ownerId
    room.winners = roomData.winners
    room.blocked = roomData.blocked
    room.revision = incomingRevision
  }

  @Mutation
  updateRoomPlayersSleep(playersSleep: { id: string, sleep: number }[]) {
    this.room?.players.forEach(player => {
      const sleepRecord = playersSleep.find(p => p.id === player.id)
      if (sleepRecord) {
        player.sleep = sleepRecord.sleep
      }
    })
  }

  @Mutation
  createGame(gameId: string) {
    if (this.room) {
      if (this.game?.id === gameId) return
      const game = new Game(gameId, this.room)
      this.game = game
    }
  }

  @Mutation
  updateGame({ gameData }: { gameData: GameDTO }) {
    if (this.room) {
      if (!this.game) {
        const game = new Game(gameData.id, this.room)
        this.game = game
      }

      const game = this.game
      if (!game) return

      const snapshotType = gameData.snapshotType ?? 'full'
      const replaceSnapshot = snapshotType === 'full'
      const incomingRevision = gameData.revision ?? -1
      const decision = decideGameSnapshot({
        id: game.id,
        sessionId: game.sessionId,
        revision: game.revision,
        round: game.round,
        mode: game.mode,
        terminal: isTerminalGameMode(game.mode)
      }, {
        id: gameData.id,
        sessionId: gameData.sessionId,
        revision: gameData.revision,
        round: gameData.round,
        mode: gameData.mode,
        terminal: gameData.mode === undefined ? undefined : isTerminalGameMode(gameData.mode)
      }, snapshotType)

      if (!decision.apply) return

      const samePhase = game.sessionId === gameData.sessionId &&
        game.revision === incomingRevision &&
        game.round === gameData.round

      game.timer = nextTimerValue(
        game.timer,
        gameData.timer,
        samePhase,
        gameData.generatedAt
      )
      game.generatedAt = Math.max(game.generatedAt, gameData.generatedAt ?? 0)

      if (decision.timerOnly) return

      if (gameData.revision !== undefined) {
        game.revision = gameData.revision
      }

      if (gameData.sessionId !== undefined) {
        game.sessionId = gameData.sessionId
      }
      
      if (replaceSnapshot || gameData.hint !== undefined) {
        game.hint = gameData.hint ?? ''
      }

      if (replaceSnapshot || gameData.currentPlayerId !== undefined) {
        game.currentPlayerId = gameData.currentPlayerId ?? ''
      }

      if (replaceSnapshot || gameData.nextPlayerId !== undefined) {
        game.nextPlayerId = gameData.nextPlayerId ?? ''
      }

      if (gameData.crashReason !== undefined) {
        game.crashReason = gameData.crashReason
      }

      if (gameData.mode !== undefined) {
        game.mode = gameData.mode
      }

      if (gameData.round !== undefined) {
        game.round = gameData.round
      }

      if (gameData.status !== undefined) {
        game.status = gameData.status
      }

      if (gameData.extra !== undefined) {
        game.extra = gameData.extra
      }

      if (gameData.isSuperTurn !== undefined) {
        game.isSuperTurn = gameData.isSuperTurn
      }

      if (gameData.difficulty !== undefined) {
        game.difficulty = gameData.difficulty
      }
      
      if (replaceSnapshot || gameData.statuses !== undefined) {
        game.statuses = gameData.statuses ?? []
      }
      
      if (replaceSnapshot || gameData.tmpCards !== undefined) {
        game.tmpCards = gameData.tmpCards ?? null
      }

      if (replaceSnapshot || gameData.winners !== undefined) {
        game.winners = gameData.winners ?? []
      }

      if (replaceSnapshot || gameData.guesed !== undefined) {
        game.guesed = gameData.guesed ?? []
      }

      if (replaceSnapshot || gameData.answer !== undefined) {
        game.answer = gameData.answer
      }

      if (replaceSnapshot && gameData.animation === undefined) {
        game.resetAnimation()
      }
      else if (gameData.animation !== undefined) {
        game.animation = gameData.animation
      }

      if (replaceSnapshot || gameData.logs !== undefined) {
        game.logs = gameData.logs ?? []
      }

      if (gameData.random !== undefined) {
        game.random = gameData.random
      }

      if (gameData.teamLives !== undefined) {
        game.teamLives = gameData.teamLives
      }

      if (gameData.teamProgress !== undefined) {
        game.teamProgress = gameData.teamProgress
      }

      if (gameData.teamLostLives !== undefined) {
        game.teamLostLives = gameData.teamLostLives
      }

      if (gameData.teamFinalBonus !== undefined) {
        game.teamFinalBonus = gameData.teamFinalBonus
      }
      if (gameData.teamPreviousRecord !== undefined) {
        game.teamPreviousRecord = gameData.teamPreviousRecord
      }

      if (replaceSnapshot || gameData.players) {
        const oldPlayers = game.players.map(p => p)
        game.players = (gameData.players ?? []).map(player => {
          const playerData = {
            ...player,
            status: player.status,
            isSuperTurn: player.isSuperTurn,
            cardsCount: player.cardsCount,
          }
          
          const hasPlayer = oldPlayers.find(p => p.id === player.id)

          if (replaceSnapshot) {
            playerData.cards = (player.cards ?? []).map(card => new Card(card))
          }
          else if (player.cards !== undefined) {
            playerData.cards = player.cards.map(card => new Card(card))
          }
          else {
            if (hasPlayer) {
              playerData.cards = hasPlayer.cards.map(c => c)
            }
          }

          if (hasPlayer) {
            return new GamePlayer({
              ...playerData,
              status: player.status,
              isSuperTurn: player.isSuperTurn,
            })
          }
          else return new GamePlayer(playerData)
        })
      }

    }
  }

  @Mutation
  switchHiglightWord({ id, value }: { id: number, value: boolean }) {
    this.room?.players.forEach(player => {
      const card = player.cards.find(card => card.words.some(word => word.id === id))
      if (card) {
        const word = card.words.find(word => word.id === id)
        if (word) {
          console.log(word.active, value)
          word.active = value
        }
      }
    })
  }

  @Mutation
  resetAll() {
    this.room = null
    this.game = null
  }

  @Mutation
  resetGame() {
    this.game = null
  }

  @Mutation
  resetRoom() {
    this.room = null
  }
}

export { SystemModule }
